export interface ProfileAddressFields {
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

export interface ProfileLocationContent {
  location?: string;
  addresses?: ProfileAddressFields[];
}

export interface ProfileLocationDisplay {
  locationLabel?: string;
  addressChip?: string;
}

export const formatAddress = (
  address?: ProfileAddressFields
): string | undefined => {
  if (!address) return undefined;
  const line = address.address?.trim();
  const cityState = [address.city, address.state]
    .filter(Boolean)
    .join(", ")
    .trim();
  const value = [line, cityState, address.zip]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+,/g, ",")
    .trim();
  return value || undefined;
};

export const formatStateCountry = (
  address?: ProfileAddressFields
): string | undefined => {
  if (!address) return undefined;
  const value = [address.state, address.country]
    .filter(Boolean)
    .join(", ")
    .trim();
  return value || undefined;
};

/** Only a street line counts as a full address for the Address chip. */
export const isSubstantiveStructuredAddress = (
  address?: ProfileAddressFields
): boolean => Boolean(address?.address?.trim());

const coarseLocationLabel = (
  locationText: string | undefined,
  structured: ProfileAddressFields | undefined,
  fullAddress: string | undefined,
  stateCountry: string | undefined
): string | undefined => {
  if (locationText) return locationText;
  if (structured?.city?.trim() || structured?.zip?.trim()) {
    return fullAddress || stateCountry;
  }
  return stateCountry || fullAddress;
};

/**
 * Splits profile geography between the meta "Location" line and the Address chip.
 * Social location strings (e.g. "Austin, Texas") and city/state without a street
 * line stay in Location only. A street line shows in the Address chip; when
 * content.location is also present, both are shown as-is.
 */
export const resolveProfileLocationDisplay = (
  content: ProfileLocationContent
): ProfileLocationDisplay => {
  const structured = content.addresses?.[0];
  const fullAddress = formatAddress(structured);
  const stateCountry = formatStateCountry(structured);
  const locationText = content.location?.trim();

  if (fullAddress && isSubstantiveStructuredAddress(structured)) {
    return {
      locationLabel: locationText || stateCountry || undefined,
      addressChip: fullAddress,
    };
  }

  return {
    locationLabel: coarseLocationLabel(
      locationText,
      structured,
      fullAddress,
      stateCountry
    ),
    addressChip: undefined,
  };
};
